<template>
  <drawer-card v-model="showDrawer" :drawer-width="showPieChart ? '35rem' : '80%'">
    <div slot="card-content" class="pt-8 px-8 pr-20">
      <area-chart ref="assistanceResolutionChart"
                  id="assistance-resolution-chart"
                  :data="sortedData"
                  title="Assistance Resolution"
                  :title-hint="hint"
                  :axis-names="{ x: 'Total Request' }"
                  :axis-x-range="axisXRange"
                  :is-loading="loaders.assistanceResolutions || loaders.assistanceResolutionsUpdate"
                  :min-range="3600000"
                  @zoom="$emit('load-assistance-resolution', $event)"
                  @show-drawer="handleShowDrawer"
      >
        <template #tooltip="{ showTooltip, tooltipProps, axisXValues }">
          <assistance-resolution-chart-tooltip v-if="showTooltip && !showDrawer"
                                               :tooltip-props="tooltipProps"
                                               :axis-x-values="axisXValues"
                                               :data="sortedData"
                                               @show-detail="handleShowDrawer"
                                               @mouseleave.native="hideTooltip"
          />
        </template>
      </area-chart>
    </div>

    <div slot="drawer-content" class="h-full">
      <div>
        <assistance-type-pie-chart v-if="showPieChart"/>
        <assistance-resolution-chart-overlay v-else
                                             :data="sortedData"
                                             :resolution-filter-data.sync="resolutionFilterData"
                                             :bot-id="botId"
        />
      </div>
    </div>
  </drawer-card>
</template>

<script>
import DrawerCard from '@/components/base-components/DrawerCard'
import AreaChart from '@/components/analytics/charts/AreaChart'
import AssistanceResolutionChartTooltip from '@/components/analytics/charts/AssistanceResolutionChartTooltip'
import { CHART_TEXT } from '@/utils/text.constants'
import { GRANULARITY_REQUEST } from '@/utils/filter.constants'
import analyticsFilterModule from '@/store/modules/analytics-filter.module'
import AssistanceResolutionChartOverlay from '@/components/analytics/charts/AssistanceResolutionChartOverlay'
import requestOutcomeMapper from '@/utils/request-outcome.mapper'
import AssistanceTypePieChart from '@/components/analytics/charts/AssistanceTypePieChart'
import analyticsModule from '@/store/modules/analytics.module'

export default {
  name: 'AssistanceResolutionChart',

  components: {
    AssistanceResolutionChartTooltip,
    DrawerCard,
    AreaChart,
    AssistanceResolutionChartOverlay,
    AssistanceTypePieChart
  },

  props: {
    botId: {
      type: String,
      required: true
    },
    loaders: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      showDrawer: false,
      showPieChart: false,
      resolutionFilterData: {}
    }
  },

  computed: {
    granularity: analyticsFilterModule.getGranularity,
    dateFrom: analyticsFilterModule.getDateFrom,
    dateTo: analyticsFilterModule.getDateTo,
    assistanceResolutions: analyticsModule.getAssistanceResolutions,

    axisXRange() {
      return { from: +new Date(this.dateFrom), to: +new Date(this.dateTo) }
    },

    sortedData() {
      return requestOutcomeMapper.sortOutcomes(this.assistanceResolutions)
    },

    hint() {
      const {
        REQUEST_ASSISTANCE_TYPE_HINT,
        CONTACT_ASSISTANCE_TYPE_HINT,
        REQUEST_HINT,
        CONTACT_HINT
      } = CHART_TEXT
      return GRANULARITY_REQUEST === this.granularity ?
        `${REQUEST_ASSISTANCE_TYPE_HINT}${REQUEST_HINT}` :
        `${CONTACT_ASSISTANCE_TYPE_HINT}${CONTACT_HINT}`
    }
  },

  watch: {
    'loaders.assistanceResolutions': {
      immediate: false,
      async handler(isLoading) {
        if (!isLoading) return

        this.showDrawer = false
        this.hideTooltip()

        // time for mapping data and getting them into chart
        await this.$nextTick()
        this.$refs.assistanceResolutionChart.setAxisXValue(this.axisXRange.from, this.axisXRange.to)
      }
    }
  },

  methods: {
    handleShowDrawer(resolutionFilterData) {
      if (resolutionFilterData) {
        this.resolutionFilterData = resolutionFilterData
        this.showPieChart = false
      } else {
        this.showPieChart = true
      }
      this.showDrawer = true
    },

    hideTooltip(e) {
      if (e?.toElement.id && e.toElement.id !== 'assistance-resolution-chart') return

      this.$refs.assistanceResolutionChart.showTooltip = false
    }
  }
}
</script>
